import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAICredits } from '@/hooks/useAICredits';
import { useLicenseActivations } from '@/hooks/supabase/useLicenseActivations';
import {
  AI_CREDIT_PRESETS,
  DEFAULT_AUTO_TOPUP_AMOUNT_CENTS,
  creditsFromDollars,
  formatUsdFromCents,
} from '@/components/billing/mapleosProducts';
import { AlertTriangle, Bot, CreditCard, HardDriveDownload, Loader2, Monitor, RefreshCw, ShieldAlert, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import AICreditEmbeddedPayment from '@/components/billing/AICreditEmbeddedPayment';
import { isElectronCheckoutEnvironment } from '@/lib/electronStripe';

type AICreditManagerProps = {
  embedded?: boolean;
  desktopTheme?: 'light' | 'dark';
};

export default function AICreditManager({ embedded = false, desktopTheme }: AICreditManagerProps) {
  const {
    account,
    loading,
    saving,
    topupLoading,
    fetchAccount,
    updateSettings,
    startTopupCheckout,
  } = useAICredits();
  const {
    licenses: activationLicenses,
    primaryLicenseId,
    currentDeviceActivationId,
    currentDeviceActivated,
    limitReached,
    loading: activationsLoading,
    actionLoading: activationActionLoading,
    revokingId,
    activateCurrentDevice,
    revokeActivation,
  } = useLicenseActivations();

  const [customTopup, setCustomTopup] = useState('25');
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  const [autoTopupEnabled, setAutoTopupEnabled] = useState(account.autoTopupEnabled);
  const [lowCreditThreshold, setLowCreditThreshold] = useState(account.lowCreditThreshold);
  const [autoTopupAmountCents, setAutoTopupAmountCents] = useState(account.autoTopupAmountCents);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedTopupAmountCents, setSelectedTopupAmountCents] = useState<number | null>(null);

  useEffect(() => {
    setAutoTopupEnabled(account.autoTopupEnabled);
    setLowCreditThreshold(account.lowCreditThreshold);
    setAutoTopupAmountCents(account.autoTopupAmountCents);
  }, [account.autoTopupAmountCents, account.autoTopupEnabled, account.lowCreditThreshold]);

  const trialUsed = useMemo(() => Math.max(0, 250 - account.trialCreditsRemaining), [account.trialCreditsRemaining]);
  const isDesktopLight = desktopTheme === 'light';
  const isElectron = isElectronCheckoutEnvironment();
  const activationLicenseMap = useMemo(
    () => new Map(activationLicenses.map((license) => [license.id, license])),
    [activationLicenses]
  );

  const saveSettings = async () => {
    await updateSettings({
      autoTopupEnabled,
      lowCreditThreshold,
      autoTopupAmountCents,
    });
  };

  const customTopupCents = useMemo(() => creditsFromDollars(Number(customTopup || 0)), [customTopup]);

  const startTopup = (amountCents: number) => {
    if (amountCents < 500) {
      return;
    }

    if (isElectron) {
      setSelectedTopupAmountCents(amountCents);
      setPaymentModalOpen(true);
      return;
    }

    void startTopupCheckout(amountCents, savePaymentMethod);
  };

  const statusTone = account.exceeded
    ? isDesktopLight
      ? 'text-red-700 border-red-200 bg-red-50'
      : 'text-red-400 border-red-500/20 bg-red-500/10'
    : account.low
      ? isDesktopLight
        ? 'text-amber-800 border-amber-200 bg-amber-50'
        : 'text-amber-300 border-amber-500/20 bg-amber-500/10'
      : isDesktopLight
        ? 'text-emerald-700 border-emerald-200 bg-emerald-50'
        : 'text-emerald-300 border-emerald-500/20 bg-emerald-500/10';

  const summaryCardClass = isDesktopLight
    ? 'min-w-0 border-border/70 bg-card text-card-foreground shadow-sm'
    : 'min-w-0 border-white/10 bg-[linear-gradient(180deg,rgba(20,28,44,0.96),rgba(10,16,28,0.98))] text-white shadow-[0_24px_80px_rgba(0,0,0,0.3)]';

  const summaryHeaderClass = isDesktopLight ? 'border-b pb-5' : 'border-b border-white/10 pb-5';
  const summaryDescriptionClass = isDesktopLight
    ? 'mt-2 max-w-2xl break-words text-muted-foreground'
    : 'mt-2 max-w-2xl break-words text-white/65';
  const summaryButtonClass = isDesktopLight
    ? ''
    : 'border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white';
  const neutralActionButtonClass = isDesktopLight
    ? ''
    : 'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground';
  const amountInputClass = 'flex h-10 w-full rounded-md border px-3 py-2 text-base md:text-sm focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50';
  const desktopLightInputStyle = isDesktopLight
    ? {
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))',
      }
    : undefined;
  const summaryLabelClass = isDesktopLight ? 'text-sm text-muted-foreground' : 'text-sm text-white/60';
  const summaryUnitClass = isDesktopLight ? 'pb-1 text-sm text-muted-foreground' : 'pb-1 text-sm text-white/55';
  const summaryBadgeClass = isDesktopLight ? '' : 'border-white/15 text-white/75';
  const summaryMetricCardClass = isDesktopLight
    ? 'rounded-2xl border bg-muted/30 p-4'
    : 'rounded-2xl border border-white/10 bg-white/[0.04] p-4';
  const summaryMetricLabelClass = isDesktopLight ? 'text-sm text-muted-foreground' : 'text-sm text-white/55';
  const summaryMetricSubClass = isDesktopLight ? 'text-sm text-muted-foreground' : 'text-sm text-white/45';
  const exceededAlertClass = isDesktopLight
    ? 'rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700'
    : 'rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-100';
  const exceededAlertTextClass = isDesktopLight ? 'mt-2 text-red-700/90' : 'mt-2 text-red-100/85';

  return (
    <>
      <div className={cn('w-full min-w-0 space-y-6', embedded ? 'overflow-x-hidden p-4' : 'container mx-auto max-w-7xl px-4 py-8')}>
        <div className="grid min-w-0 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <Card className={summaryCardClass}>
            <CardHeader className={summaryHeaderClass}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {account.autoTopupEnabled && (
                      <Badge variant="outline" className={cn(!isDesktopLight && 'border-white/20 text-white/80')}>
                        Auto top-up enabled
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="mt-3 text-2xl">MapleOS usage billing</CardTitle>
                  <CardDescription className={summaryDescriptionClass}>
                    MapleOS provides trial AI credits to all users. After that, your credit balance can be topped up as needed.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={() => fetchAccount()} disabled={loading} className={summaryButtonClass}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="grid min-w-0 gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="min-w-0">
                  <p className={summaryLabelClass}>Credits remaining</p>
                  <div className="mt-2 flex flex-wrap items-end gap-3">
                    <div className="text-5xl font-semibold tracking-tight">{account.availableCredits.toLocaleString()}</div>
                    <div className={summaryUnitClass}>credits</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge variant="outline" className={summaryBadgeClass}>
                      Trial remaining: {account.trialCreditsRemaining.toLocaleString()}
                    </Badge>
                    <Badge variant="outline" className={summaryBadgeClass}>
                      Purchased balance: {account.balanceCredits.toLocaleString()}
                    </Badge>
                  </div>
                </div>
                <div className={cn('min-w-0 rounded-2xl border px-4 py-3 text-sm break-words', statusTone)}>
                  {account.exceeded ? 'You have exceeded your AI usage.' : account.low ? 'Your AI credits are running low.' : 'Your AI credits are in a healthy range.'}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className={summaryMetricCardClass}>
                  <div className={summaryMetricLabelClass}>Low credit threshold</div>
                  <div className="mt-2 text-2xl font-semibold">{account.lowCreditThreshold}</div>
                  <div className={summaryMetricSubClass}>credits</div>
                </div>
                <div className={summaryMetricCardClass}>
                  <div className={summaryMetricLabelClass}>Auto top-up amount</div>
                  <div className="mt-2 text-2xl font-semibold">{formatUsdFromCents(account.autoTopupAmountCents)}</div>
                  <div className={summaryMetricSubClass}>{account.autoTopupAmountCents.toLocaleString()} credits</div>
                </div>
                <div className={summaryMetricCardClass}>
                  <div className={summaryMetricLabelClass}>Trial used</div>
                  <div className="mt-2 text-2xl font-semibold">{trialUsed.toLocaleString()}</div>
                  <div className={summaryMetricSubClass}>of 250 starter credits</div>
                </div>
              </div>

              {account.exceeded && (
                <div className={exceededAlertClass}>
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    AI usage paused until you top up or enable auto top-up.
                  </div>
                  <p className={exceededAlertTextClass}>
                    Paid AI APIs in MapleOS require credits after the trial balance is used. Add credits below to continue using cloud AI models.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0 border-border/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Billing and top-ups
              </CardTitle>
              <CardDescription className="break-words">
                Add credits instantly, save a payment method, and enable auto top-up if you want MapleOS to reload credits automatically when you run low.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="mb-3 text-sm font-medium">Quick top-up</div>
                <div className="grid grid-cols-2 gap-2">
                  {AI_CREDIT_PRESETS.map((amountCents) => (
                    <Button
                      key={amountCents}
                      variant="outline"
                      disabled={topupLoading}
                      onClick={() => startTopup(amountCents)}
                      className="justify-between"
                    >
                      <span>{formatUsdFromCents(amountCents)}</span>
                      <span className="text-muted-foreground">{amountCents.toLocaleString()} credits</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="custom-topup">Custom amount in USD</Label>
                <div className="flex gap-2">
                  <input
                    id="custom-topup"
                    type="number"
                    min="5"
                    step="1"
                    value={customTopup}
                    style={desktopLightInputStyle}
                    className={cn(amountInputClass, 'ring-offset-background focus-visible:ring-ring', isDesktopLight ? 'placeholder:text-muted-foreground' : 'border-input bg-background text-foreground placeholder:text-muted-foreground')}
                    onChange={(event) => setCustomTopup(event.target.value)}
                  />
                  <Button
                    variant="outline"
                    className={neutralActionButtonClass}
                    disabled={topupLoading || customTopupCents < 500}
                    onClick={() => startTopup(customTopupCents)}
                  >
                    {topupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Top up'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {customTopupCents.toLocaleString()} credits will be added.
                </p>
              </div>

              <div className="flex items-center justify-between rounded-xl border p-3">
                <div>
                  <div className="font-medium">Save payment method</div>
                  <div className="text-sm text-muted-foreground">
                    Your next top-up can save the payment method for future auto reloads.
                  </div>
                </div>
                <Switch checked={savePaymentMethod} onCheckedChange={setSavePaymentMethod} />
              </div>

              <Separator />

              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-xl border p-3">
                  <div>
                    <div className="font-medium">Enable auto top-up</div>
                    <div className="text-sm text-muted-foreground">
                      MapleOS will automatically buy more credits when you run low.
                    </div>
                  </div>
                  <Switch checked={autoTopupEnabled} onCheckedChange={setAutoTopupEnabled} />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <Label>Alert me when credits reach</Label>
                    <span className="text-muted-foreground">{lowCreditThreshold} credits</span>
                  </div>
                  <Slider min={25} max={500} step={25} value={[lowCreditThreshold]} onValueChange={(value) => setLowCreditThreshold(value[0])} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="auto-topup-amount">Auto top-up amount</Label>
                  <input
                    id="auto-topup-amount"
                    type="number"
                    min={10}
                    step={5}
                    value={Math.round(autoTopupAmountCents / 100)}
                    style={desktopLightInputStyle}
                    className={cn(amountInputClass, 'ring-offset-background focus-visible:ring-ring', isDesktopLight ? 'placeholder:text-muted-foreground' : 'border-input bg-background text-foreground placeholder:text-muted-foreground')}
                    onChange={(event) => setAutoTopupAmountCents(Math.max(DEFAULT_AUTO_TOPUP_AMOUNT_CENTS, Number(event.target.value || 0) * 100))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {autoTopupAmountCents.toLocaleString()} credits will be added each time MapleOS reloads your balance.
                  </p>
                </div>

                <Button onClick={saveSettings} disabled={saving} className="w-full" variant="outline">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save billing settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid min-w-0 gap-6 xl:grid-cols-[1fr_1fr]">
          <Card className="min-w-0">
            <CardHeader>
              <CardTitle>Recent AI usage</CardTitle>
              <CardDescription className="break-words">Paid API usage is tracked by tokens and converted into credit consumption.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {account.usageEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No paid usage recorded yet.</p>
              ) : (
                account.usageEvents.slice(0, 8).map((event) => (
                  <div key={event.id} className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium">{event.model}</div>
                      <div className="text-muted-foreground">
                        {event.provider}
                        {event.source ? ` · ${event.source}` : ''}
                        {event.phase ? ` · ${event.phase}` : ''}
                        {' · '}
                        {new Date(event.created_at).toLocaleString()}
                      </div>
                      {event.run_title && (
                        <div className="text-xs text-muted-foreground">{event.run_title}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div>{event.credits_used} credits</div>
                      <div className="text-muted-foreground">{event.tokens_used.toLocaleString()} tokens</div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardHeader>
              <CardTitle>Credit ledger</CardTitle>
              <CardDescription className="break-words">Every trial grant, usage deduction, and top-up is recorded here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {account.ledger.length === 0 ? (
                <p className="text-sm text-muted-foreground">No ledger entries yet.</p>
              ) : (
                account.ledger.slice(0, 10).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium capitalize">{entry.entry_type.replace(/_/g, ' ')}</div>
                      <div className="text-muted-foreground">
                        {entry.metadata?.source ? `${entry.metadata.source} · ` : ''}
                        {new Date(entry.created_at).toLocaleString()}
                      </div>
                      {entry.metadata?.run_title && (
                        <div className="text-xs text-muted-foreground">
                          {entry.metadata.run_title}
                          {entry.metadata.phase ? ` · ${entry.metadata.phase}` : ''}
                        </div>
                      )}
                    </div>
                    <div className={cn('font-medium', entry.credits_delta >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                      {entry.credits_delta >= 0 ? '+' : ''}{entry.credits_delta.toLocaleString()} credits
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Installers and entitlements</CardTitle>
            <CardDescription className="break-words">
              Your active annual editions determine which installers you can access. Pro and Enterprise include the bare metal installer.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {activationLicenses.length > 0 && (
              <Alert className={cn(
                'border-amber-200 bg-amber-50 text-amber-950 [&>svg]:text-amber-700',
                !isDesktopLight && 'border-amber-900/60 bg-amber-950/40 text-amber-100'
              )}>
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>Device activation limits protect your license</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    MapleOS Pro and Enterprise are tied to a limited number of active devices. Revoke old devices if you need to move your license.
                  </p>
                  {!currentDeviceActivated && (
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => void activateCurrentDevice(true)} disabled={activationActionLoading} size="sm">
                        {activationActionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Activate this device
                      </Button>
                      {limitReached && (
                        <span className="self-center text-sm text-muted-foreground">
                          You have reached the active device limit for your primary license. Revoke one below, then activate this device.
                        </span>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {account.licenses.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-5 text-sm text-muted-foreground break-words">
                No paid annual product licenses yet. MapleOS 26 web access and desktop installer are still available.
              </div>
            ) : (
              account.licenses.map((license) => {
                const activationLicense = activationLicenseMap.get(license.id);
                const activeActivations = activationLicense?.activations.filter((activation) => !activation.revoked_at) ?? [];
                const isPrimaryLicense = primaryLicenseId === license.id;

                return (
                  <div key={license.id} className="rounded-2xl border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-lg font-semibold">{license.edition} {license.version}</div>
                        <div className="text-sm text-muted-foreground">Purchased {new Date(license.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline"><Monitor className="mr-1 h-3.5 w-3.5" /> Web installer</Badge>
                        <Badge variant="outline"><Bot className="mr-1 h-3.5 w-3.5" /> Desktop installer</Badge>
                        {license.includes_bare_metal_installer && (
                          <Badge><HardDriveDownload className="mr-1 h-3.5 w-3.5" /> Bare metal installer</Badge>
                        )}
                      </div>
                    </div>

                    {activationLicense && (
                      <div className="mt-4 space-y-3 rounded-2xl border bg-muted/30 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="font-medium">Device activations</div>
                            <div className="text-sm text-muted-foreground">
                              {activationLicense.active_device_count} of {activationLicense.device_limit} active devices in use.
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {isPrimaryLicense && currentDeviceActivated && currentDeviceActivationId ? (
                              <Badge>This device is active</Badge>
                            ) : null}
                            {isPrimaryLicense && !currentDeviceActivated ? (
                              <Button size="sm" variant="outline" onClick={() => void activateCurrentDevice(true)} disabled={activationActionLoading}>
                                {activationActionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Activate this device
                              </Button>
                            ) : null}
                          </div>
                        </div>

                        {isPrimaryLicense && limitReached && !currentDeviceActivated && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
                            Revoke one of the active devices below before activating this device.
                          </div>
                        )}

                        {activationsLoading ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading device activations…
                          </div>
                        ) : activeActivations.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No active devices are registered for this license yet.</p>
                        ) : (
                          activeActivations.map((activation) => {
                            const isCurrentDevice = currentDeviceActivationId === activation.id;

                            return (
                              <div key={activation.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-background px-3 py-3 text-sm">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium">{activation.device_name}</span>
                                    {isCurrentDevice ? <Badge variant="outline">Current device</Badge> : null}
                                  </div>
                                  <div className="text-muted-foreground">
                                    Last seen {new Date(activation.last_seen_at).toLocaleString()}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => void revokeActivation(activation.id)}
                                  disabled={revokingId === activation.id}
                                >
                                  {revokingId === activation.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                                  Revoke
                                </Button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {!embedded && (
              <>
                <Separator />
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
                  <span>Need the installer page?</span>
                  <Button asChild variant="outline">
                    <Link to="/downloads">Open Downloads</Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add AI credits</DialogTitle>
            <DialogDescription>
              Complete your top-up without leaving MapleOS. Your credits will appear as soon as the payment succeeds.
            </DialogDescription>
          </DialogHeader>

          {selectedTopupAmountCents ? (
            <AICreditEmbeddedPayment
              amountCents={selectedTopupAmountCents}
              savePaymentMethod={savePaymentMethod}
              dark={!isDesktopLight}
              onSuccess={async () => {
                setPaymentModalOpen(false);
                await fetchAccount();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}